import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-all select-none disabled:pointer-events-none disabled:opacity-50 hover:not-disabled:-translate-y-px hover:not-disabled:shadow-sm active:not-disabled:translate-y-0",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-dark)]",
        secondary:
          "bg-[var(--color-bg-tertiary)] text-[var(--color-text)] hover:bg-[var(--color-border)]",
        success:
          "bg-[var(--color-success)] text-white hover:bg-green-600",
        danger:
          "bg-[var(--color-danger)] text-white hover:bg-red-600",
        ghost:
          "bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text)]",
        icon:
          "bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text)] p-1.5 rounded-md",
        outline:
          "border border-[var(--color-border)] bg-transparent text-[var(--color-text)] hover:bg-[var(--color-bg-secondary)]",
        link:
          "text-[var(--color-primary)] underline-offset-4 hover:underline bg-transparent",
        sidebar:
          "bg-transparent border-none text-white/80 cursor-pointer text-left transition-all duration-150 hover:not-disabled:translate-y-0 hover:not-disabled:shadow-none hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]",
        "sidebar-icon":
          "bg-transparent border-none text-white/50 cursor-pointer transition-[opacity,color] duration-150 hover:not-disabled:translate-y-0 hover:not-disabled:shadow-none hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]",
      },
      size: {
        /* `icon` existe porque o variant de mesmo nome NÃO consegue se defender:
           o `p-1.5` dele é emitido ANTES do `px-3 py-1.5` do size na folha do
           Tailwind, então o padding do size sempre vence e o botão só-ícone sai
           retangular. Por isso quase todo uso de `variant="icon"` no repo carrega
           um `w-8 h-8 p-0` de compensação à mão. Com `size="icon"` a compensação
           mora no primitivo. Migrar os usos antigos é fila de adoção. */
        icon: "h-8 w-8 p-0",
        /* 28px é a geometria do menu "..." de linha de lista, que o
           DESIGN_SYSTEM.md já documentava como `w-7 h-7 p-0` repetido à mão. */
        "icon-sm": "h-7 w-7 p-0",
        sm: "px-2 py-1 text-xs",
        md: "px-3 py-1.5 text-[13px]",
        lg: "px-4 py-2 text-sm",
        xl: "px-6 py-3.5 text-base font-semibold rounded-lg active:scale-[0.98]",
        sidebar: "flex items-center gap-3 w-full px-4 py-2.5 text-[length:var(--font-base)]",
        "sidebar-sm": "p-1 rounded-[var(--radius-sm)] flex items-center justify-center",
        "sidebar-collapsed": "justify-center p-2.5",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: React.ComponentType<{ size?: number }>
  iconPosition?: "left" | "right"
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, icon: Icon, iconPosition = "left", children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {Icon && iconPosition === "left" && <Icon size={16} />}
        {children && <span className="contents">{children}</span>}
        {Icon && iconPosition === "right" && <Icon size={16} />}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
